import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Edit, Save, X, ChevronDown, ChevronUp, Settings } from "lucide-react";
import { format } from "date-fns";
import { PublishConfirmationDialog } from "@/components/release/PublishConfirmationDialog";

interface ReleaseNote {
  id?: string;
  version: string;
  release_date: string;
  title: string;
  description: string;
  bug_fixes: Array<{ description: string }>;
  feature_improvements: Array<{ description: string }>;
  is_published: boolean;
}

export default function InfoManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [publishingRelease, setPublishingRelease] = useState<{ id: string | null; data: ReleaseNote; isNew?: boolean } | null>(null);
  const [formData, setFormData] = useState<ReleaseNote>({
    version: "",
    release_date: new Date().toISOString().split("T")[0],
    title: "",
    description: "",
    bug_fixes: [],
    feature_improvements: [],
    is_published: false,
  });
  const [newBugFix, setNewBugFix] = useState("");
  const [newFeature, setNewFeature] = useState("");

  const { data: releases, isLoading } = useQuery({
    queryKey: ["release-notes-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("release_notes")
        .select("*")
        .order("release_date", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: ReleaseNote) => {
      const { error } = await supabase.from("release_notes").insert([data]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["release-notes-all"] });
      queryClient.invalidateQueries({ queryKey: ["release-notes"] });
      toast({ title: "Release note created successfully" });
      resetForm();
    },
    onError: (error) => {
      toast({ title: "Error creating release note", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data, sendNotifications }: { id: string; data: Partial<ReleaseNote>; sendNotifications?: boolean }) => {
      const { error } = await supabase.from("release_notes").update(data).eq("id", id);
      if (error) throw error;
      
      // If publishing and sendNotifications is true, trigger notification edge function
      if (sendNotifications && data.is_published) {
        const releaseData = releases?.find(r => r.id === id);
        if (releaseData) {
          console.log('🚀 Triggering release notifications for:', releaseData.version);
          const { error: notifError } = await supabase.functions.invoke('send-release-notification', {
            body: {
              releaseId: id,
              version: releaseData.version,
              title: releaseData.title,
              description: releaseData.description,
              featureImprovements: releaseData.feature_improvements || [],
              bugFixes: releaseData.bug_fixes || []
            }
          });
          
          if (notifError) {
            console.error('Error sending notifications:', notifError);
            toast({ 
              title: "Release updated but notifications failed", 
              description: notifError.message, 
              variant: "destructive" 
            });
          }
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["release-notes-all"] });
      queryClient.invalidateQueries({ queryKey: ["release-notes"] });
      toast({ title: "Release note updated successfully" });
      setEditingId(null);
    },
    onError: (error) => {
      toast({ title: "Error updating release note", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("release_notes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["release-notes-all"] });
      queryClient.invalidateQueries({ queryKey: ["release-notes"] });
      toast({ title: "Release note deleted successfully" });
    },
    onError: (error) => {
      toast({ title: "Error deleting release note", description: error.message, variant: "destructive" });
    },
  });

  const resetForm = () => {
    setFormData({
      version: "",
      release_date: new Date().toISOString().split("T")[0],
      title: "",
      description: "",
      bug_fixes: [],
      feature_improvements: [],
      is_published: false,
    });
    setIsCreating(false);
    setEditingId(null);
    setNewBugFix("");
    setNewFeature("");
  };

  const handleEdit = (release: any) => {
    setFormData(release);
    setEditingId(release.id);
  };

  const handleSave = () => {
    // Check if we're creating a new release as published
    if (isCreating && formData.is_published) {
      // Show confirmation dialog for new published release
      setPublishingRelease({ id: null, data: formData, isNew: true });
      setShowPublishDialog(true);
      return;
    }
    
    // Check if we're trying to publish an existing draft
    const currentRelease = releases?.find(r => r.id === editingId);
    const wasPublished = currentRelease?.is_published || false;
    const willBePublished = formData.is_published;
    
    if (!wasPublished && willBePublished && editingId) {
      // Show confirmation dialog before publishing existing draft
      setPublishingRelease({ id: editingId, data: formData, isNew: false });
      setShowPublishDialog(true);
    } else {
      // Not publishing or already published, save directly
      if (editingId) {
        updateMutation.mutate({ id: editingId, data: formData, sendNotifications: false });
      } else {
        createMutation.mutate(formData);
      }
    }
  };

  const handlePublishConfirm = async (sendNotifications: boolean) => {
    if (publishingRelease) {
      if (publishingRelease.isNew) {
        // Create new release first
        const { data: newRelease, error: createError } = await supabase
          .from("release_notes")
          .insert([publishingRelease.data])
          .select()
          .single();
        
        if (createError) {
          toast({ 
            title: "Error creating release note", 
            description: createError.message, 
            variant: "destructive" 
          });
          setPublishingRelease(null);
          return;
        }
        
        // Send notifications if requested
        if (sendNotifications && newRelease) {
          console.log('🚀 Triggering release notifications for new release:', newRelease.version);
          const { error: notifError } = await supabase.functions.invoke('send-release-notification', {
            body: {
              releaseId: newRelease.id,
              version: newRelease.version,
              title: newRelease.title,
              description: newRelease.description,
              featureImprovements: newRelease.feature_improvements || [],
              bugFixes: newRelease.bug_fixes || []
            }
          });
          
          if (notifError) {
            console.error('Error sending notifications:', notifError);
            toast({ 
              title: "Release created but notifications failed", 
              description: notifError.message, 
              variant: "destructive" 
            });
          } else {
            toast({ title: "Release published and notifications sent" });
          }
        } else {
          toast({ title: "Release note created successfully" });
        }
        
        // Refresh data
        queryClient.invalidateQueries({ queryKey: ["release-notes-all"] });
        queryClient.invalidateQueries({ queryKey: ["release-notes"] });
        resetForm();
      } else {
        // Update existing draft
        updateMutation.mutate({ 
          id: publishingRelease.id!, 
          data: publishingRelease.data, 
          sendNotifications 
        });
      }
      setPublishingRelease(null);
    }
  };

  const addBugFix = () => {
    if (newBugFix.trim()) {
      setFormData({
        ...formData,
        bug_fixes: [...formData.bug_fixes, { description: newBugFix }],
      });
      setNewBugFix("");
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        feature_improvements: [...formData.feature_improvements, { description: newFeature }],
      });
      setNewFeature("");
    }
  };

  const removeBugFix = (index: number) => {
    setFormData({
      ...formData,
      bug_fixes: formData.bug_fixes.filter((_, i) => i !== index),
    });
  };

  const removeFeature = (index: number) => {
    setFormData({
      ...formData,
      feature_improvements: formData.feature_improvements.filter((_, i) => i !== index),
    });
  };

  const handleNewRelease = () => {
    // Calculate next version number
    if (releases && releases.length > 0) {
      const versions = releases.map(r => r.version).filter(v => v);
      const latestVersion = versions.sort((a, b) => {
        const aParts = a.split('.').map(Number);
        const bParts = b.split('.').map(Number);
        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          const aNum = aParts[i] || 0;
          const bNum = bParts[i] || 0;
          if (aNum !== bNum) return bNum - aNum;
        }
        return 0;
      })[0];
      
      if (latestVersion) {
        const versionParts = latestVersion.split('.');
        const lastPart = parseInt(versionParts[versionParts.length - 1] || '0');
        versionParts[versionParts.length - 1] = (lastPart + 1).toString();
        const nextVersion = versionParts.join('.');
        
        setFormData({
          ...formData,
          version: nextVersion,
        });
      }
    } else {
      // First release, default to 1.0.0
      setFormData({
        ...formData,
        version: "1.0.0",
      });
    }
    
    setIsCreating(true);
  };

  const headerActions = (
    <Button 
      variant="outline"
      onClick={handleNewRelease} 
      disabled={isCreating || editingId !== null}
      className="bg-white border-black text-black hover:bg-slate-50"
    >
      <Plus className="h-4 w-4 mr-2" />
      New Release
    </Button>
  );

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader 
            icon={Settings}
            title="Manage Release Notes"
            description="Create and manage platform release notes"
            actions={headerActions}
          />
          <div className="p-4 sm:p-6 lg:p-12 xl:px-16" style={{ paddingTop: "8.5rem" }}>
            <div className="w-full max-w-none">

            {(isCreating || editingId) && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>{editingId ? "Edit Release Note" : "Create New Release Note"}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="version">Version</Label>
                      <Input
                        id="version"
                        placeholder="e.g., 1.0.0"
                        value={formData.version}
                        onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="release_date">Release Date</Label>
                      <Input
                        id="release_date"
                        type="date"
                        value={formData.release_date}
                        onChange={(e) => setFormData({ ...formData, release_date: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      placeholder="Release title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Brief description of this release"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Feature Improvements</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a feature improvement"
                        value={newFeature}
                        onChange={(e) => setNewFeature(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && addFeature()}
                      />
                      <Button type="button" onClick={addFeature}>Add</Button>
                    </div>
                    <ul className="space-y-2 mt-2">
                      {formData.feature_improvements.map((feature, idx) => (
                        <li key={idx} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                          <span>{feature.description}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFeature(idx)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <Label>Bug Fixes</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a bug fix"
                        value={newBugFix}
                        onChange={(e) => setNewBugFix(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && addBugFix()}
                      />
                      <Button type="button" onClick={addBugFix}>Add</Button>
                    </div>
                    <ul className="space-y-2 mt-2">
                      {formData.bug_fixes.map((fix, idx) => (
                        <li key={idx} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                          <span>{fix.description}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeBugFix(idx)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_published"
                      checked={formData.is_published}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, is_published: checked })
                      }
                    />
                    <Label htmlFor="is_published">Publish immediately</Label>
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      onClick={handleSave}
                      className="bg-white border-black text-black hover:bg-slate-50"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </Button>
                    <Button variant="outline" onClick={resetForm}>
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {isLoading ? (
              <div>Loading...</div>
            ) : (
              <div className="space-y-4">
                {releases?.map((release) => (
                  <Card key={release.id} className="cursor-pointer hover:shadow-md transition-shadow">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div 
                        className="flex-1 cursor-pointer"
                        onClick={() => setExpandedId(expandedId === release.id ? null : release.id)}
                      >
                        <CardTitle className="flex items-center gap-2">
                          {release.title}
                          <Badge 
                            variant={release.is_published ? "outline" : "outline"}
                            className="bg-white border-black text-black"
                          >
                            {release.is_published ? "Published" : "Draft"}
                          </Badge>
                          <Badge variant="outline" className="font-mono">v{release.version}</Badge>
                          {expandedId === release.id ? (
                            <ChevronUp className="h-4 w-4 ml-2" />
                          ) : (
                            <ChevronDown className="h-4 w-4 ml-2" />
                          )}
                        </CardTitle>
                        <p className="text-sm text-slate-600">
                          {format(new Date(release.release_date), "MMMM d, yyyy")}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(release)}
                          disabled={editingId !== null || isCreating}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => deleteMutation.mutate(release.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    {expandedId === release.id && (
                      <CardContent className="pt-0 space-y-6">
                        {release.description && (
                          <div>
                            <p className="text-slate-600 text-left">{release.description}</p>
                          </div>
                        )}
                        
                        {release.feature_improvements && 
                         Array.isArray(release.feature_improvements) && 
                         release.feature_improvements.length > 0 && (
                          <div>
                            <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                              <span className="text-teal-600">✨</span>
                              Feature Improvements
                            </h3>
                            <ul className="space-y-2">
                              {release.feature_improvements.map((feature: any, idx: number) => (
                                <li key={idx} className="flex items-start gap-2 text-slate-700">
                                  <span className="text-teal-500 mt-1">•</span>
                                  <span>{feature.description || feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {release.feature_improvements && 
                         release.bug_fixes && 
                         Array.isArray(release.feature_improvements) && 
                         Array.isArray(release.bug_fixes) &&
                         release.feature_improvements.length > 0 && 
                         release.bug_fixes.length > 0 && (
                          <Separator />
                        )}

                        {release.bug_fixes && 
                         Array.isArray(release.bug_fixes) && 
                         release.bug_fixes.length > 0 && (
                          <div>
                            <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                              <span className="text-amber-600">🐛</span>
                              Bug Fixes
                            </h3>
                            <ul className="space-y-2">
                              {release.bug_fixes.map((fix: any, idx: number) => (
                                <li key={idx} className="flex items-start gap-2 text-slate-700">
                                  <span className="text-amber-500 mt-1">•</span>
                                  <span>{fix.description || fix}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            )}
            </div>
          </div>
        </main>

        <PublishConfirmationDialog
          open={showPublishDialog}
          onOpenChange={setShowPublishDialog}
          onConfirm={handlePublishConfirm}
          version={publishingRelease?.data.version || ""}
          title={publishingRelease?.data.title || ""}
          alreadySentNotifications={!!releases?.find(r => r.id === publishingRelease?.id)?.notifications_sent_at}
        />
      </div>
    </SidebarProvider>
  );
}
